<template>
  <!-- LOGADO: abas Plano + Créditos. Visitante vindo de promoção de créditos
       (link externo ?coupon=... ou modal tab=credits&acionador=ModalPromotionPopup)
       também vê as abas. Demais visitantes: só o conteúdo de planos. -->
  <TabsBella
    v-if="isLoggedIn || isCreditPromoEntry"
    :tabs="tabsList"
    v-model="activeTab"
  >
    <template #planos>
      <!-- O topo (título/upgrade) agora é responsabilidade do próprio
           AssinePlansGridDesktop/AssinePlansCarouselMobile; aqui só o cupom. -->
      <AssineBannerCupom
        discount="50"
        v-if="route.query && route.query['coupon-discount']"
      />
      <AssinePlansGridDesktop v-if="!isMobile" :order="order" />
      <AssinePlansCarouselMobile v-else :order="order" />
    </template>
    <template #creditos>
      <AssineCredits
        v-if="plansCredits"
        :plansCredits="plansCredits"
        @showModalMessages="(val) => $emit('showModalMessages', val)"
      />
    </template>
  </TabsBella>

  <!-- NÃO logado: apenas planos (banner cupom + grid/carrossel), sem abas. -->
  <div v-else>
    <AssineBannerCupom
      discount="50"
      v-if="route.query && route.query['coupon-discount']"
    />
    <AssinePlansGridDesktop v-if="!isMobile" :order="order" />
    <AssinePlansCarouselMobile v-else :order="order" />
  </div>
</template>

<script lang="ts" setup>
import TabsBella from "@/components/TabsBella.vue";
import AssinePlansGridDesktop from "@/components/Assine/AssinePlansGridDesktop.vue";
import AssinePlansCarouselMobile from "@/components/Assine/AssinePlansCarouselMobile.vue";
import AssineCredits from "@/components/Assine/AssineCredits.vue";
import AssineBannerCupom from "./AssineBannerCupom.vue";

const storePlanCredits = planCreditStore();
const storePlan = planStore();
const storeUser = userStore();
const plansCredits = computed(() => storePlanCredits.plansCredits);
// Logado = mostra abas (planos + créditos); não logado = só planos.
const isLoggedIn = computed(() => storeUser.isLoggedIn);
const route = useRoute();

// Desktop x mobile coerente entre SSR e cliente (evita o flash de desktop antes
// do mobile no F5). Ver composables/useIsMobileDevice.ts.
// Nesta tela usamos 970px (em vez de 770): entre 770–970 (iPads/tablets) o grid
// de 3 cards fica espremido, então mantemos o carrossel de 1 card até 970.
const isMobile = useIsMobileDevice(970);

const props = defineProps({
  order: {
    required: false,
  },
  translate: {
    type: Object,
    required: true,
  },
});
defineEmits(["showModalMessages"]);

// Link externo de promoção de créditos (?coupon=XXX, sem tab=credits):
// o backend aplica desconto SOMENTE aos planos de crédito quando o cupom é
// de créditos, então discount>0 é sinal exclusivo desse tipo de promoção.
const isCreditCouponPromo = computed(() => {
  if (route.query.tab) return false; // tab explícito tem prioridade
  return plansCredits.value?.some((p) => Number(p.discount) > 0) ?? false;
});

// VISITANTE que chegou por promoção de CRÉDITOS deve ver as abas (para ter
// acesso à de créditos). Dois caminhos de entrada:
//  - Modal de promoção de créditos: ?tab=credits&acionador=ModalPromotionPopup
//    (o ModalPromotionPopup só adiciona tab=credits quando a promo é de crédito).
//  - Cupom de promoção de créditos presente na URL (?coupon=XXX) OU já gravado
//    no store de plans (sessionStorage, via setCoupon no layout). O store cobre
//    o retorno ao /assine sem o ?coupon na URL (entrou pelo link, saiu, navegou
//    e voltou). Em ambos, isCreditCouponPromo garante que o desconto recai nos
//    planos de crédito. Demais visitantes seguem sem abas.
const isCreditPromoEntry = computed(() => {
  const fromCreditModal =
    route.query.tab === "credits" &&
    route.query.acionador === "ModalPromotionPopup";
  const hasCreditCoupon =
    (!!route.query.coupon || !!storePlan.coupon) && isCreditCouponPromo.value;
  return fromCreditModal || hasCreditCoupon;
});

// Inicializa apenas com o parâmetro da URL (idêntico no SSR e no cliente) para
// evitar hydration mismatch: plansCredits vem do sessionStorage e não existe
// no servidor, então não pode influenciar o primeiro render.
const activeTab = ref(route.query.tab == "credits" ? 1 : 0);

// Após a hidratação (só no cliente): se já for promoção de créditos, seleciona
// a aba. Como é pós-montagem, vira uma atualização reativa sem mismatch.
onMounted(() => {
  if (isCreditCouponPromo.value) activeTab.value = 1;
});

// plansCredits chega de forma assíncrona (getPlansCredits no layout);
// quando o desconto for aplicado, alterna para a aba de créditos.
watch(isCreditCouponPromo, (isCredit) => {
  if (isCredit) activeTab.value = 1;
});

const tabsList = [
  {
    label: props.translate.assineOptions1,
    slot: "planos",
  },
  {
    label: props.translate.assineOptions2,
    slot: "creditos",
  },
];
</script>
<style scoped lang="css">
.tabs >>> .active {
  height: unset !important;
}
</style>
